function normalize(value) {
    return String(value || "").trim().toLowerCase();
}

function hasAny(text, words) {
    const source = normalize(text);
    return words.some((word) => source.includes(word));
}

function getCombinedText(form) {
    return [
        form.industry,
        form.subIndustry,
        form.description,
        form.coreServices,
        form.projectTypes,
        form.primaryAudience
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

export function getFieldExplanation(fieldName, form) {
    const combined = getCombinedText(form);
    const industry = normalize(form.industry);
    const goal = normalize(form.goalOfWebsite);
    const offerings = normalize(form.coreServices + " " + form.projectTypes);

    switch (fieldName) {
        case "businessModel":
            if (hasAny(combined, ["academy", "training", "program", "coach", "admission"])) {
                return "Suggested because your business details look like a program-based or academy-style operation.";
            }
            if (hasAny(combined, ["property", "listing", "real estate", "broker", "builder", "locality"])) {
                return "Suggested because your business details point to a real-estate or listings-driven business.";
            }
            if (hasAny(combined, ["software", "saas", "platform", "automation", "dashboard"])) {
                return "Suggested because your description and offerings indicate a software or SaaS-style business.";
            }
            if (hasAny(combined, ["clinic", "doctor", "patient", "treatment", "health"])) {
                return "Suggested because your business details indicate a healthcare-oriented service business.";
            }
            if (hasAny(combined, ["law", "legal", "advocate", "firm", "consultation"])) {
                return "Suggested because your business details indicate a legal or consultation-led business.";
            }
            return "Suggested from your industry, description, and offerings.";

        case "primaryWebsiteMode":
            if (goal.includes("generate leads") || goal.includes("build trust")) {
                return "Suggested because your stated goal indicates a trust and conversion-first experience.";
            }
            if (hasAny(offerings, ["program", "course", "academy", "training"])) {
                return "Suggested because users will likely need to understand programs before taking action.";
            }
            if (hasAny(offerings, ["listing", "property", "locality"])) {
                return "Suggested because discovery and search are important for this type of business.";
            }
            if (hasAny(offerings, ["product", "pricing", "feature", "software"])) {
                return "Suggested because the website should highlight product value early.";
            }
            return "Suggested from the business goal and content structure.";

        case "primaryEntityType":
            if (hasAny(offerings, ["program", "course", "batch", "coach"])) {
                return "Suggested because your core offerings are program-oriented.";
            }
            if (hasAny(offerings, ["listing", "property", "locality", "apartment", "villa"])) {
                return "Suggested because your content revolves around properties or listings.";
            }
            if (hasAny(offerings, ["product", "collection", "catalog"])) {
                return "Suggested because your website appears product-led.";
            }
            if (hasAny(offerings, ["feature", "pricing", "integration", "dashboard"])) {
                return "Suggested because your website appears feature-led.";
            }
            return "Suggested from your offerings and content structure.";

        case "locationFocus":
            if (hasAny(combined, ["ahmedabad", "delhi", "mumbai", "pune", "city", "local", "nearby"])) {
                return "Suggested because your business appears to serve a local or city-focused audience.";
            }
            if (hasAny(combined, ["india", "nationwide", "national"])) {
                return "Suggested because your business appears to operate across a wider geographic region.";
            }
            if (hasAny(combined, ["global", "international", "remote", "worldwide"])) {
                return "Suggested because your business appears to target a broader global audience.";
            }
            return "Suggested from your business coverage and audience context.";

        case "contentStyleHint":
            if (goal.includes("build trust")) {
                return "Suggested because trust-building content is important for your stated website goal.";
            }
            if (goal.includes("drive sales")) {
                return "Suggested because your website goal needs stronger conversion and offer clarity.";
            }
            if (hasAny(industry, ["legal", "healthcare"])) {
                return "Suggested because this industry usually performs better with trust-heavy and authority-focused content.";
            }
            if (hasAny(industry, ["saas", "software"])) {
                return "Suggested because this industry benefits from clear, product-led messaging.";
            }
            return "Suggested from your industry and website objective.";

        case "themeKey":
            return "Suggested from the industry, content tone, and expected visual direction.";

        case "tone":
            return "Suggested from your business category and expected audience communication style.";

        case "brandStyle":
            return "Suggested from your market positioning and visual direction.";

        case "pages":
            if (hasAny(industry, ["academy"])) {
                return "Suggested because users typically evaluate programs, coaches, and admissions before converting.";
            }
            if (hasAny(industry, ["real estate"])) {
                return "Suggested because users typically browse listings and locality-focused content first.";
            }
            if (hasAny(industry, ["healthcare"])) {
                return "Suggested because users usually need services, doctors, and appointment paths.";
            }
            if (hasAny(industry, ["legal"])) {
                return "Suggested because practice areas and consultation paths are critical for legal websites.";
            }
            if (hasAny(industry, ["saas", "software"])) {
                return "Suggested because SaaS buyers usually compare features, pricing, and value early.";
            }
            return "Suggested from your business type and likely user journey.";

        default:
            return "Suggested from your business inputs.";
    }
}